// Dismissible "Get the app" banner for iOS visitors. Renders above the
// site chrome when:
//   - the visitor is on iOS (iPhone / iPad / iPod via userAgent)
//   - they haven't dismissed it (localStorage `hm.iosBanner.dismissed`)
//   - they're not already in a standalone PWA / installed shell
// Designed as a backstop to Apple's native Smart App Banner (meta tag
// in index.html), which is Safari-only and easy to permanently dismiss.
// Our banner reaches Chrome/Firefox on iOS too and shows up after a
// soft refresh of localStorage.

import { useEffect, useState } from 'react';
import { v } from '../constants';

const STORAGE_KEY = 'hm.iosBanner.dismissed';
const APP_STORE_URL = 'https://apps.apple.com/us/app/hexponents-daily-math-puzzle/id6766731447';

function detectIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPadOS 13+ reports as Mac in UA; the touch-point check disambiguates
  // a real Mac (0 touch points) from an iPad in desktop-mode (≥1).
  const isIosUa = /iPhone|iPod/.test(ua);
  const isIpadDesktop = ua.includes('Macintosh') && navigator.maxTouchPoints > 1;
  return isIosUa || /iPad/.test(ua) || isIpadDesktop;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS sets navigator.standalone when launched from a Home-Screen icon.
  // The display-mode check covers modern PWA installs across browsers.
  const nav = navigator as Navigator & { standalone?: boolean };
  if (nav.standalone) return true;
  try {
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch { return false; }
}

export function IosAppBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Dev-only escape hatch: `?ios=1` forces the banner on so we can
    // QA the layout in a desktop preview. Stripped out by build-time
    // tree-shaking when import.meta.env.DEV is false.
    const forced = import.meta.env.DEV
      && new URLSearchParams(window.location.search).has('ios');
    if (!forced && !detectIos()) return;
    if (isStandalone()) return;
    try {
      if (localStorage.getItem(STORAGE_KEY) === '1') return;
    } catch { /* ignore — show anyway */ }
    setVisible(true);
  }, []);

  if (!visible) return null;

  function dismiss() {
    setVisible(false);
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
  }

  return (
    <div
      role="region"
      aria-label="Get the Hexponents app"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 12px',
        background: v('paperDeep'),
        borderBottom: `1px solid ${v('rule')}`,
        fontSize: 13,
        color: v('inkSoft'),
      }}
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss download banner"
        style={{
          background: 'transparent',
          border: 'none',
          padding: 4,
          color: v('inkMute'),
          cursor: 'pointer',
          fontSize: 16,
          lineHeight: 1,
        }}
      >×</button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: v('ink') }}>Hexponents</div>
        <div style={{ fontSize: 11, color: v('inkMute') }}>Daily logic puzzle · Free on the App Store</div>
      </div>
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          textDecoration: 'none',
          background: v('ink'),
          color: v('paper'),
          padding: '6px 14px',
          borderRadius: 999,
          fontWeight: 600,
          fontSize: 13,
          flexShrink: 0,
        }}
      >Get</a>
    </div>
  );
}
